import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchGraphQL } from '../../../../lib/graphql';
import {
  Card,
  CardContent,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Text,
  Button,
  Badge,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@tonic/shared/components/ui';
import { ArrowLeft, Edit2, Save } from 'lucide-react';

const GET_CUSTOMER = `
  query GetCustomer($id: ID!) {
    customer(id: $id) {
      id
      email
      firstName
      lastName
      loyaltyTier
      totalOrders
      lifetimeValue
      lastOrderDate
      marketingOptIn
      reCommerceActivity {
        totalReturns
        totalRepairs
        totalResales
      }
      orders {
        id
        total
        status
        createdAt
      }
      metrics {
        engagementScore
        reCommerceScore
      }
    }
  }
`;

const UPDATE_CUSTOMER = `
  mutation UpdateCustomer($id: ID!, $input: UpdateCustomerInput!) {
    updateCustomer(id: $id, input: $input) {
      id
      loyaltyTier
      marketingOptIn
    }
  }
`;

export function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    loyaltyTier: '',
    marketingOptIn: false
  });

  const { data: customerData, isLoading, error } = useQuery({
    queryKey: ['customer', id],
    queryFn: () => fetchGraphQL(GET_CUSTOMER, { id }),
  });

  const updateCustomerMutation = useMutation({
    mutationFn: (variables) => fetchGraphQL(UPDATE_CUSTOMER, variables),
    onSuccess: () => {
      queryClient.invalidateQueries(['customer', id]);
    },
  });

  const handleSave = async () => {
    try {
      await updateCustomerMutation.mutate({
        variables: {
          id,
          input: formData
        }
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating customer:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
      </div>
    );
  }

  if (error || !customerData?.customer) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/customers')}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <Text className="text-red-800">
            {error?.message || 'Failed to load customer details'}
          </Text>
        </div>
      </div>
    );
  }

  const { customer } = customerData;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/customers')}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div>
            <Text variant="h1" className="text-2xl font-bold">
              {customer.firstName} {customer.lastName}
            </Text>
            <Text variant="body" className="text-muted-foreground">{customer.email}</Text>
          </div>
        </div>
        {isEditing ? (
          <Button
            onClick={handleSave}
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            Save Changes
          </Button>
        ) : (
          <Button
            variant="outline"
            onClick={() => setIsEditing(true)}
            className="gap-2"
          >
            <Edit2 className="h-4 w-4" />
            Edit
          </Button>
        )}
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="orders">Orders</TabsTrigger>
          <TabsTrigger value="re-commerce">Re-Commerce</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="pt-6">
                <Text variant="h2" className="font-semibold">Customer Information</Text>
                <div className="mt-4 space-y-4">
                  <div>
                    <Text variant="body" color="muted">Loyalty Tier</Text>
                    {isEditing ? (
                      <Select
                        value={formData.loyaltyTier}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, loyaltyTier: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="STANDARD">Standard</SelectItem>
                          <SelectItem value="SILVER">Silver</SelectItem>
                          <SelectItem value="GOLD">Gold</SelectItem>
                          <SelectItem value="VIP">VIP</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant={
                        customer.loyaltyTier === 'VIP' ? 'destructive' :
                          customer.loyaltyTier === 'GOLD' ? 'secondary' :
                            customer.loyaltyTier === 'SILVER' ? 'secondary' : 'outline'
                      }>
                        {customer.loyaltyTier}
                      </Badge>
                    )}
                  </div>
                  <div>
                    <Text variant="body" color="muted">Total Orders</Text>
                    <Text className="text-2xl font-bold">{customer.totalOrders}</Text>
                  </div>
                  <div>
                    <Text variant="body" color="muted">Lifetime Value</Text>
                    <Text className="text-2xl font-bold">${customer.lifetimeValue.toFixed(2)}</Text>
                  </div>
                  <div>
                    <Text variant="body" color="muted">Last Order</Text>
                    <Text className="text-2xl font-bold">
                      {customer.lastOrderDate ? new Date(customer.lastOrderDate).toLocaleDateString() : '-'}
                    </Text>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <Text variant="h2" className="font-semibold">Engagement Metrics</Text>
                <div className="mt-4 space-y-4">
                  <div>
                    <Text variant="body" color="muted">Engagement Score</Text>
                    <Text className="text-2xl font-bold">{customer.metrics.engagementScore}%</Text>
                  </div>
                  <div>
                    <Text variant="body" color="muted">Re-Commerce Score</Text>
                    <Text className="text-2xl font-bold">{customer.metrics.reCommerceScore}%</Text>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="orders">
          <Card>
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order Number</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customer.orders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>{order.id}</TableCell>
                      <TableCell>
                        {new Date(order.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>${order.total.toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge variant={
                          order.status === 'COMPLETED' ? 'success' :
                            order.status === 'PENDING' ? 'warning' :
                              order.status === 'CANCELLED' ? 'destructive' : 'secondary'
                        }>
                          {order.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="re-commerce">
          <Card>
            <CardContent className="pt-6">
              <Text variant="h2" className="font-semibold">Re-Commerce Activity</Text>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Text variant="body" color="muted">Total Returns</Text>
                  <Text className="text-2xl font-bold">{customer.reCommerceActivity.totalReturns}</Text>
                </div>
                <div>
                  <Text variant="body" color="muted">Total Repairs</Text>
                  <Text className="text-2xl font-bold">{customer.reCommerceActivity.totalRepairs}</Text>
                </div>
                <div>
                  <Text variant="body" color="muted">Total Resales</Text>
                  <Text className="text-2xl font-bold">{customer.reCommerceActivity.totalResales}</Text>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings">
          <Card>
            <CardContent className="pt-6">
              <Text variant="h2" className="font-semibold">Customer Settings</Text>
              <div className="mt-4 space-y-4">
                <div>
                  <Text variant="body" color="muted">Marketing Communications</Text>
                  {isEditing ? (
                    <Select
                      value={formData.marketingOptIn.toString()}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, marketingOptIn: value === 'true' }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Opted In</SelectItem>
                        <SelectItem value="false">Opted Out</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant={customer.marketingOptIn ? 'default' : 'secondary'}>
                      {customer.marketingOptIn ? 'Opted In' : 'Opted Out'}
                    </Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
} 
